import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { TemplateRunDto } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/** How long to wait before looking again while another window holds the run's lease. */
export const RUN_LEASE_POLL_MS = 3_000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §4d/§4f] AN APPROVED RUN IS DRIVEN FROM THE PAGE, ONE BOUNDED BATCH PER REQUEST.
 *
 *  `POST /templates/runs/:id/advance` does one batch (inside nginx's sixty seconds) and answers the
 *  run as it now stands. This loop asks again until the run is `DONE` — so CLOSING THE TAB PAUSES the
 *  run and OPENING `?run=<id>` AGAIN RESUMES it, with no worker and nothing left half-done: the server
 *  remembers which items are finished, and each order is created or published in its own transaction.
 *
 *  ⚠️ A LEASE, NOT A LOCK THIS PAGE HOLDS. Two windows on one run each ask; the server lets ONE advance
 *  (a compare-and-set on the lease) and answers the other `leaseHeld: true` with no progress. This page
 *  then FOLLOWS — it reads the run every few seconds and asks to advance again, which succeeds the
 *  moment the other window is closed and its lease runs out.
 *
 *  ⚠️ STARTED BY THE RUN BEING `RUNNING`, NEVER BY A CLICK HANDLER — so a resumed page and a freshly
 *  approved one take the same path, and a `DONE` run is never advanced at all. The loop writes each
 *  answer into the cache with `setQueryData` ([P4]'s rule: no refetch scheduled for it to race), and a
 *  failed request STOPS it with the error on screen and a Continue button, rather than retrying blind.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const useTemplateRun = (runId: string | null) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [following, setFollowing] = useState(false);

  const run = useQuery({
    queryKey: queryKeys.templates.run(runId ?? ''),
    queryFn: ({ signal }) => api.get<TemplateRunDto>(`/templates/runs/${runId ?? ''}`, { signal }),
    enabled: runId !== null,
    staleTime: Infinity,
  });

  const running = run.data?.state === 'RUNNING';

  useEffect(() => {
    if (runId === null || !running) return undefined;
    let stopped = false;
    const key = queryKeys.templates.run(runId);

    const drive = async (): Promise<void> => {
      while (!stopped) {
        try {
          const next = await api.post<TemplateRunDto>(`/templates/runs/${runId}/advance`, {});
          if (stopped) return;
          queryClient.setQueryData(key, next);
          setFollowing(next.leaseHeld);
          if (next.state === 'DONE') return;
          if (next.leaseHeld) {
            await sleep(RUN_LEASE_POLL_MS);
            if (stopped) return;
            const fresh = await api.get<TemplateRunDto>(`/templates/runs/${runId}`);
            if (stopped) return;
            queryClient.setQueryData(key, fresh);
            if (fresh.state === 'DONE') return;
          }
        } catch (caught) {
          if (stopped) return;
          setError((caught as ApiError).message);
          return;
        }
      }
    };

    void drive();
    return () => {
      stopped = true;
    };
  }, [runId, running, attempt, queryClient]);

  return {
    run: run.data,
    isLoading: run.isPending && runId !== null,
    loadError: run.error ? (run.error as ApiError).message : null,
    error,
    following: running && following,
    /** Clears the error and asks again — the server resumes from the first pending item. */
    resume: (): void => {
      setError(null);
      setAttempt((current) => current + 1);
    },
  };
};
